/**
 * History. SPEC.md §13.1, §13.2, §8.6.
 *
 * Two questions get answered here: what the night has come to, and what happened
 * in each hand. The live hand comes from the ledger the socket has already
 * delivered; the completed hands come from GET /api/sessions/:id/history, which
 * is the only place a session's full round history exists. §13.2, §15.5
 *
 * A hand is a card rather than a table row, because the thing people want from
 * history is not a transaction log — it is "who took hand 16, and how much did
 * it cost me". So each card leads with the answer and carries the per-player
 * breakdown underneath it. The transfer rows are still there, one fold down, for
 * the argument that needs settling. §13.1, §13.2
 *
 * Who won a hand is not stored anywhere: it is read back out of who stayed
 * silent, by the same shared function that decided where the points went. §3.1, §60
 *
 * A correction never deletes, so this shows both halves of one: the inverse row
 * is marked as such, and the row it cancelled is struck through. §12.2
 *
 * Names are full and untruncated — the ring is where they get shortened. §8.6
 * It scrolls inside the sheet, never the score screen behind it. §18.2
 */

import { useEffect, useMemo, useState } from 'react';
import {
  entryOutcomeLabel,
  entryShapeTag,
  formatClockTime,
  formatHandLabel,
  formatSigned,
  handHeadline,
  inferWinner,
  isReversed,
  silentPlayers,
  type Adjustment,
  type HandEntry,
  type PlayerView,
  type RoundSummary,
  type SessionHistory,
  type Transfer,
} from '@card-counter/shared';
import { Sheet } from '@/components/ui/Sheet';
import { SignedScore } from '@/components/ui/SignedScore';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/hooks/useSession';
import { errorMessageOf, fetchHistory } from '@/lib/api';
import {
  useActiveRound,
  useAllPlayersBySeat,
  useDrinkingMode,
  useHandAdjustments,
  useLastRoundSummary,
  useMyPlayerId,
  useRecentTransfers,
  useSessionInfo,
  useSessionStore,
} from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

type NameLookup = (playerId: string) => string;

/** Withdrawn or unknown players still need a label rather than a blank. §31 */
const UNKNOWN_PLAYER = 'A player';

/**
 * The host's way back, and the only one. §12.5
 *
 * Hands close themselves the moment the table has told them enough, so by the
 * time somebody notices a mistyped number there is no report left to withdraw
 * and no snackbar still up. Deleting the hand cancels every point it moved and
 * clears what it was told, and the table enters it again.
 *
 * It lives here rather than in the host menu because the menu can only offer
 * "the last one", and the hand that needs deleting is not reliably the last one
 * — an argument about hand 12 surfaces two hands later. History is where a hand
 * is identified, so it is where a hand is deleted. §13.2
 *
 * Confirmation is inline rather than a second sheet: this is already inside one,
 * and a modal over a modal to answer one question is worse than the two taps
 * being next to the thing they act on. §51
 */
function DeleteHandButton({ roundId, roundNumber }: { roundId: string; roundNumber: number }) {
  const { voidRound, interactive } = useSession();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        disabled={!interactive}
        onClick={() => {
          setConfirming(true);
        }}
        className="min-h-tap shrink-0 rounded-control px-2 text-xs font-semibold text-negative disabled:opacity-45"
      >
        Delete
      </button>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      <span aria-hidden="true" className="text-xs text-ink-muted">
        Delete {formatHandLabel(roundNumber).toLowerCase()}?
      </span>
      <button
        type="button"
        onClick={() => {
          setConfirming(false);
        }}
        className="min-h-tap rounded-control px-2 text-xs font-semibold text-ink-muted"
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={pending || !interactive}
        aria-label={`Delete ${formatHandLabel(roundNumber)} and cancel its points`}
        onClick={() => {
          setPending(true);
          void voidRound(roundId).then(() => {
            setPending(false);
            setConfirming(false);
          });
        }}
        className="min-h-tap rounded-control border border-negative/50 bg-negative-soft px-2 text-xs font-semibold text-negative disabled:opacity-45"
      >
        {pending ? 'Deleting…' : 'Delete'}
      </button>
    </span>
  );
}

/**
 * Which hands a filter chip keeps. §13.2
 *
 * "Mine" is gone. It kept every hand this player's net was not zero in, which at
 * a table people sit down at and stay at is very nearly every hand — so it took
 * a tap to remove nothing, and the one case it did filter (hands dealt before
 * somebody arrived) is not a thing anybody goes looking for. What is left is the
 * filter with a real question behind it: which hands were corrected. §13.2, §20.2
 */
type Filter = 'all' | 'corrected';

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All hands' },
  { id: 'corrected', label: 'Corrected' },
];

// --- Rows --------------------------------------------------------------------

interface TransferRowProps {
  transfer: Transfer;
  /** The round's full set, so a cancelled row can be recognised. §12.2 */
  siblings: readonly Transfer[];
  nameFor: NameLookup;
}

function TransferRow({ transfer, siblings, nameFor }: TransferRowProps) {
  const correction = Boolean(transfer.reversesTransferId);
  const undone = isReversed(transfer, siblings);

  return (
    <li className="flex items-baseline gap-2 py-1 text-sm">
      <span data-numeric className="shrink-0 text-xs text-ink-faint">
        {formatClockTime(transfer.createdAt)}
      </span>
      <span className={`min-w-0 flex-1 ${undone ? 'text-ink-faint line-through' : 'text-ink'}`}>
        {nameFor(transfer.fromPlayerId)} <span aria-hidden="true">→</span>
        <span className="sr-only">pays</span> {nameFor(transfer.toPlayerId)}
      </span>
      <span
        data-numeric
        className={`shrink-0 font-semibold ${undone ? 'text-ink-faint line-through' : 'text-ink'}`}
      >
        {transfer.amount}
      </span>
      {correction ? (
        <span className="shrink-0 text-xs text-warn">
          <span aria-hidden="true">↶ </span>correction
        </span>
      ) : null}
      {undone ? <span className="shrink-0 text-xs text-ink-faint">cancelled</span> : null}
      {/* §30 — who reported it, when that was not the player it counts for. */}
      {transfer.actedForPlayerId ? (
        <span className="shrink-0 text-xs text-ink-faint">
          by {nameFor(transfer.createdByPlayerId)}
        </span>
      ) : null}
    </li>
  );
}

/**
 * A point movement with only one end, which only drinking mode produces. §4.6
 *
 * It has to be here or a drinking hand's breakdown would not add up: the nets
 * above include the spill and the drinks, and a detail list that showed only
 * transfers would leave a reader short by exactly those rows with nothing on
 * screen to account for the difference. §13.1
 *
 * There is no "from" and no "to" — the whole point of the row is that it has
 * only one side — so it reads as a phrase rather than as an arrow.
 */
function AdjustmentRow({
  adjustment,
  siblings,
  nameFor,
}: {
  adjustment: Adjustment;
  siblings: readonly Adjustment[];
  nameFor: NameLookup;
}) {
  const correction = Boolean(adjustment.reversesAdjustmentId);
  const undone = siblings.some((row) => row.reversesAdjustmentId === adjustment.id);
  const name = nameFor(adjustment.playerId);

  return (
    <li className="flex items-baseline gap-2 py-1 text-sm">
      <span data-numeric className="shrink-0 text-xs text-ink-faint">
        {formatClockTime(adjustment.createdAt)}
      </span>
      <span className={`min-w-0 flex-1 ${undone ? 'text-ink-faint line-through' : 'text-ink'}`}>
        {adjustment.kind === 'drink'
          ? `${name} finished a drink`
          : adjustment.kind === 'everyone-loss'
            ? `${name} paid for the table`
            : `${name} did not take the rest`}
      </span>
      <span
        data-numeric
        className={`shrink-0 font-semibold ${
          undone ? 'text-ink-faint line-through' : adjustment.amount > 0 ? 'text-positive' : 'text-negative'
        }`}
      >
        {formatSigned(adjustment.amount)}
      </span>
      {correction ? (
        <span className="shrink-0 text-xs text-warn">
          <span aria-hidden="true">↶ </span>correction
        </span>
      ) : null}
      {undone ? <span className="shrink-0 text-xs text-ink-faint">cancelled</span> : null}
    </li>
  );
}

/** One player's line in a hand's breakdown. §13.2 */
function BreakdownRow({
  name,
  net,
  entry,
  isWinner,
  drinkingMode,
}: {
  name: string;
  net: number;
  entry: HandEntry | undefined;
  isWinner: boolean;
  /** What the shape did, which is not the same at both kinds of table. §4.6 */
  drinkingMode: boolean;
}) {
  return (
    <li className="flex items-center gap-2 py-0.5 text-sm">
      <span
        aria-hidden="true"
        className="grid size-7 shrink-0 place-items-center rounded-full bg-raised text-[0.7rem] font-bold text-ink-muted"
      >
        {isWinner ? '👑' : name.slice(0, 1).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 truncate text-ink">{name}</span>
      {entry ? (
        <span aria-hidden="true" className="shrink-0 text-[0.65rem] text-ink-faint">
          {entry.shape === 'TO_EVERYONE'
            ? entryOutcomeLabel({ shape: entry.shape, amount: entry.amount, drinkingMode })
            : `said ${String(entry.amount)}`}
        </span>
      ) : null}
      <span
        className={[
          'shrink-0 rounded-full px-2 py-0.5 text-xs font-bold',
          net > 0
            ? 'bg-positive-soft text-positive'
            : net < 0
              ? 'bg-negative-soft text-negative'
              : 'bg-raised text-ink-muted',
        ].join(' ')}
      >
        <SignedScore value={net} silent />
      </span>
      <span className="sr-only">
        {name} {net === 0 ? 'broke even' : net > 0 ? `took ${String(net)}` : `paid ${String(-net)}`}
      </span>
    </li>
  );
}

// --- Hand card ---------------------------------------------------------------

interface HandCardProps {
  summary: RoundSummary;
  transfers: readonly Transfer[];
  players: readonly PlayerView[];
  nameFor: NameLookup;
  /** The host may delete a hand the table wants to play again. §12.5 */
  canDelete: boolean;
}

function HandCard({ summary, transfers, players, nameFor, canDelete }: HandCardProps) {
  const { round, entries, adjustments } = summary;
  /*
    A past hand is described by the rule that settled it, which means it has to be
    described as the kind of table it was played at. The mode is fixed when the
    table is created and cannot change under a hand, so today's answer is also the
    answer that applied then. §4.6
  */
  const drinkingMode = useDrinkingMode();

  /**
   * A hand is described with the same rule that settled it, which needs to know
   * who has left the table as well as who it was dealt to. §31, §3.1
   *
   * Reading today's roster cannot change what a closed hand says: a hand only
   * closes with exactly one player silent, and anyone who had not reported by
   * then was already gone. What it does fix is the hand that closed *because*
   * somebody left — described without them it has two silent players and no
   * winner, which is not what the ledger did. §60
   */
  const departed = players.filter((player) => player.isRemoved).map((player) => player.id);

  /**
   * A deleted hand has no reports left and every row it wrote is answered, so
   * every derivation below reads it as a hand nobody played. Only the stamp can
   * tell the two apart, and the difference is worth a sentence: one is a quiet
   * hand, the other is a correction the whole table agreed to. §12.5, §13.2
   */
  const deleted = Boolean(round.voidedAt);

  const winnerPlayerId = inferWinner(entries, round.participantIds, departed);
  const headline = deleted
    ? 'Deleted by the host'
    : handHeadline({
        entries,
        participantIds: round.participantIds,
        departedPlayerIds: departed,
        drinkingMode,
        nameOf: nameFor,
      });

  /**
   * A hand can close with reports that never settled — more than one player
   * silent, so no winner could be told. Nothing moved and the ledger balances,
   * but what those players typed is still on the record and saying so is the
   * difference between a hand that was quiet and one that was never finished. §3.1
   */
  const needsWinner = entries.some((entry) => entry.shape === 'TO_WINNER');
  const unsettled = !deleted && needsWinner && winnerPlayerId === null;

  const entryOfPlayer = new Map(entries.map((entry) => [entry.playerId, entry]));
  const nets = players
    .map((player) => ({ player, net: summary.netByPlayerId[player.id] ?? 0 }))
    // Everyone who moved points is listed, including anyone since removed. §31
    .filter((row) => row.net !== 0 || entryOfPlayer.has(row.player.id) || !row.player.isRemoved)
    .sort((a, b) => b.net - a.net || a.player.seatIndex - b.player.seatIndex);

  const corrections = transfers.filter((transfer) => transfer.reversesTransferId).length;
  const shape = entries.find((entry) => entry.shape === 'TO_EVERYONE') ?? entries[0];

  return (
    <li className="flex flex-col gap-2 rounded-card border border-line bg-surface px-3 py-3">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          data-numeric
          className="grid size-9 shrink-0 place-items-center rounded-control bg-raised text-xs font-bold text-ink-muted"
        >
          {round.number}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-ink">
            {headline}
            {corrections > 0 ? (
              <span className="ml-1.5 text-[0.65rem] font-normal text-warn">corrected</span>
            ) : null}
          </h4>
          <p data-numeric className="truncate text-xs text-ink-faint">
            {formatClockTime(round.voidedAt ?? round.completedAt ?? round.startedAt)} ·{' '}
            {deleted
              ? 'points cancelled'
              : entries.length === 0
                ? 'nothing reported'
                : `${String(entries.length)} of ${String(round.participantIds.length)} reported`}
          </p>
        </div>
        <span className="sr-only">{formatHandLabel(round.number)}</span>
      </div>

      {deleted ? (
        <p className="rounded-control bg-negative-soft px-2 py-1 text-xs text-warn">
          Everything this hand moved was taken back off, with a matching row each. The table
          entered it again in a later hand.
        </p>
      ) : null}

      {unsettled ? (
        <p className="rounded-control bg-negative-soft px-2 py-1 text-xs text-warn">
          {silentPlayers(entries, round.participantIds, departed).length} players never reported, so this hand
          had no winner and nothing moved.
        </p>
      ) : null}

      {nets.length > 0 ? (
        <ul className="flex list-none flex-col border-t border-line pt-1.5">
          {nets.map(({ player, net }) => (
            <BreakdownRow
              key={player.id}
              name={player.name}
              net={net}
              entry={entryOfPlayer.get(player.id)}
              isWinner={player.id === winnerPlayerId}
              drinkingMode={drinkingMode}
            />
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {shape ? (
          <span
            className={[
              'rounded-full px-2 py-1 text-[0.6rem] font-bold tracking-wide',
              shape.shape === 'TO_EVERYONE'
                ? 'bg-negative-soft text-negative'
                : 'bg-raised text-ink-muted',
            ].join(' ')}
          >
            {entryShapeTag(shape.shape, round.participantIds.length)}
          </span>
        ) : (
          <span />
        )}

        <span className="flex items-center gap-1">
          {/* Both kinds of row under one count, because both are point changes
              and a reader checking a net has to see all of them. §4.6, §13.1 */}
          {transfers.length + adjustments.length > 0 ? (
            <details className="text-ink-muted">
              <summary className="min-h-tap cursor-pointer py-2 text-xs">
                {transfers.length + adjustments.length === 1
                  ? '1 point change'
                  : `${String(transfers.length + adjustments.length)} point changes`}
              </summary>
              <ul className="flex list-none flex-col">
                {transfers.map((transfer) => (
                  <TransferRow
                    key={transfer.id}
                    transfer={transfer}
                    siblings={transfers}
                    nameFor={nameFor}
                  />
                ))}
                {adjustments.map((adjustment) => (
                  <AdjustmentRow
                    key={adjustment.id}
                    adjustment={adjustment}
                    siblings={adjustments}
                    nameFor={nameFor}
                  />
                ))}
              </ul>
            </details>
          ) : null}

          {/* Already deleted hands have nothing left to cancel. §12.5 */}
          {canDelete && !deleted ? (
            <DeleteHandButton roundId={round.id} roundNumber={round.number} />
          ) : null}
        </span>
      </div>
    </li>
  );
}

// --- Drawer ------------------------------------------------------------------

export function HistoryDrawer() {
  const open = useUiStore((state) => state.historyOpen);
  const close = useUiStore((state) => state.closeHistory);

  const session = useSessionInfo();
  const sessionId = session?.id ?? null;
  const activeRound = useActiveRound();
  const players = useAllPlayersBySeat();
  const transfers = useRecentTransfers();
  const handAdjustments = useHandAdjustments();
  // What the live hand's empty state is allowed to promise. §4.6
  const drinkingMode = useDrinkingMode();
  const lastRoundSummary = useLastRoundSummary();
  const myPlayerId = useMyPlayerId();
  const totals = useSessionStore((state) => state.totalsByPlayerId);

  // Server-computed, and re-checked on every mutation: hiding the control is a
  // courtesy, not the rule. §27, §39
  const canDelete = useSessionStore((state) => state.permissions?.canVoidRound ?? false);

  const [history, setHistory] = useState<SessionHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  /**
   * Fetched on open, and again once a hand completes while the drawer is up. Not
   * on every sequence bump: a report changes the live list, which the socket has
   * already delivered. §13.2, §44
   */
  const completedStamp = lastRoundSummary?.round.id ?? null;
  useEffect(() => {
    if (!open || sessionId === null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void fetchHistory(sessionId, controller.signal).then(
      (result) => {
        setHistory(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (controller.signal.aborted) return;
        // Friendly shared copy, never a status line or a stack. §35
        setError(errorMessageOf(err));
        setLoading(false);
      },
    );
    return () => {
      controller.abort();
    };
  }, [open, sessionId, completedStamp]);

  /**
   * The fetched history carries removed players too, so a name in an old hand
   * still resolves after someone leaves the table. §31, §13.2
   */
  const nameFor = useMemo<NameLookup>(() => {
    const names = new Map<string, string>();
    for (const player of history?.players ?? []) names.set(player.id, player.name);
    for (const player of players) names.set(player.id, player.name);
    return (playerId: string) => names.get(playerId) ?? UNKNOWN_PLAYER;
  }, [history, players]);

  const knownPlayers = history?.players ?? players;

  const currentTransfers = useMemo(
    () =>
      activeRound === null
        ? []
        : transfers.filter((transfer) => transfer.roundId === activeRound.id),
    [transfers, activeRound],
  );

  const completedRounds = (history?.rounds ?? []).filter(
    (summary) => summary.round.status === 'completed',
  );

  const shown = completedRounds.filter((summary) => {
    if (filter === 'corrected') {
      return (history?.transfersByRoundId[summary.round.id] ?? []).some(
        (transfer) => transfer.reversesTransferId,
      );
    }
    return true;
  });

  const myTotal = myPlayerId === null ? 0 : (totals[myPlayerId] ?? 0);

  return (
    <Sheet open={open} onClose={close} title="History">
      <div className="flex flex-col gap-4 pb-2">
        {/* Where the night stands, in the same panel the score screen leads with,
            so the two read as the same object. §13.3 */}
        <section className="flex items-center justify-between gap-3 rounded-card bg-panel px-4 py-3 text-panel-ink">
          <div>
            <p className="text-[0.625rem] font-bold tracking-[0.14em] text-panel-muted uppercase">
              Your total
            </p>
            <p
              data-numeric
              className={`text-2xl font-bold ${
                myTotal > 0
                  ? 'text-panel-positive'
                  : myTotal < 0
                    ? 'text-panel-negative'
                    : 'text-panel-ink'
              }`}
            >
              {formatSigned(myTotal)}
            </p>
          </div>
          <div className="text-right">
            <p data-numeric className="text-lg font-bold">
              {completedRounds.length}
            </p>
            <p className="text-[0.65rem] text-panel-muted">
              {completedRounds.length === 1 ? 'hand played' : 'hands played'}
            </p>
          </div>
        </section>

        <section aria-label="This hand" className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">
              {activeRound ? formatHandLabel(activeRound.number) : 'This hand'}
            </h3>
            {/* Deleting the hand being played empties it where it stands: same
                number, no reports, no points. §12.5 */}
            {canDelete && activeRound ? (
              <DeleteHandButton roundId={activeRound.id} roundNumber={activeRound.number} />
            ) : null}
          </div>

          {activeRound === null ? (
            <p className="text-sm text-ink-muted">No hand is running right now.</p>
          ) : currentTransfers.length + handAdjustments.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {/*
                The second sentence is the ordinary table's rule, and a drinking
                table breaks it twice: a finished drink writes a row mid-hand
                with nobody silent, and a hand one player pays for destroys
                points without a winner ever being inferred. §4.6, §4.3
              */}
              No points have moved yet this hand.{' '}
              {drinkingMode
                ? 'They move when the hand settles, or the moment somebody finishes a drink.'
                : 'They move when only one player is left having reported nothing.'}
            </p>
          ) : (
            // Newest first: the change someone is checking is almost always the
            // last one. The row format is §13.1's.
            <ul className="flex list-none flex-col">
              {currentTransfers.map((transfer) => (
                <TransferRow
                  key={transfer.id}
                  transfer={transfer}
                  siblings={currentTransfers}
                  nameFor={nameFor}
                />
              ))}
              {/* Drinking mode's own rows: a drink somebody has already finished
                  this hand, and the spill once a winner is inferable. §4.6 */}
              {handAdjustments.map((adjustment) => (
                <AdjustmentRow
                  key={adjustment.id}
                  adjustment={adjustment}
                  siblings={handAdjustments}
                  nameFor={nameFor}
                />
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Completed hands" className="flex flex-col gap-2">
          <div
            role="group"
            aria-label="Filter hands"
            className="scroll-area flex gap-2 overflow-x-auto pb-1"
          >
            {FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={filter === option.id}
                onClick={() => {
                  setFilter(option.id);
                }}
                className={[
                  'min-h-tap shrink-0 rounded-full border px-3 text-xs font-semibold whitespace-nowrap',
                  filter === option.id
                    ? 'border-accent bg-accent text-accent-ink'
                    : 'border-line bg-surface text-ink-muted',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>

          {loading && history === null ? (
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Spinner size="sm" label="Loading history…" />
              <span>Loading the earlier hands…</span>
            </div>
          ) : null}

          {error === null ? null : (
            <p role="alert" className="text-sm text-negative">
              {error}
            </p>
          )}

          {history !== null && completedRounds.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Nothing finished yet. Completed hands show up here with everyone's net for the
              hand.
            </p>
          ) : null}

          {history !== null && completedRounds.length > 0 && shown.length === 0 ? (
            <p className="text-sm text-ink-muted">No hands match that filter.</p>
          ) : null}

          <ul className="flex list-none flex-col gap-2">
            {shown.map((summary) => (
              <HandCard
                key={summary.round.id}
                summary={summary}
                transfers={history?.transfersByRoundId[summary.round.id] ?? []}
                players={knownPlayers}
                nameFor={nameFor}
                canDelete={canDelete}
              />
            ))}
          </ul>
        </section>
      </div>
    </Sheet>
  );
}
